import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Stack } from '@chakra-ui/react';

import { useCreateInterviewAgent } from '@/store/createInterviewAgent';
import { Client } from '@/types/Client';
import { Job } from '@/types/Job';
import { CandidateWithJobContext } from '@/types/Candidate';

import Stepper from './Stepper';
import { SessionDetailsSection } from './SessionDetailsSection';
import { QuestionEditorSection } from './QuestionEditorSection';
import { ReviewSection } from './ReviewSection';
import { SuccessSection } from './SuccessSection';

interface UpdatedScreeningData {
  session_name: string;
  deadline: number;
  interview_title: string;
  interview_description: string;
  retake_limit: number;
  session_time_limit: number;
  questions: string[];
}

interface CreateUpdateAgentIndexProps {
  onUpdateSuccess?: (updatedScreening: UpdatedScreeningData) => void;
}

interface LocationState {
  prefillData?: {
    client: Client;
    job: Job;
    candidate: CandidateWithJobContext;
  };
}

export default function CreateUpdateAgentIndex({ onUpdateSuccess }: CreateUpdateAgentIndexProps) {
  const location = useLocation();
  const { step, reset, isUpdateMode, fixedCandidate, initializeJobInterview } =
    useCreateInterviewAgent();
  const hasInitialized = useRef(false);
  const prevStep = useRef(step);

  // Initialize store with prefilled data or reset on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      const state = location.state as LocationState;

      // Initialize with prefilled data if provided
      if (state?.prefillData && !isUpdateMode && !fixedCandidate) {
        const { client, job, candidate } = state.prefillData;
        initializeJobInterview(client, job, candidate);
      } else if (!isUpdateMode && !fixedCandidate) {
        reset();
      }

      hasInitialized.current = true;
    }
    // Dependencies intentionally limited - only run on mount or when key flags change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to top on step change
  useEffect(() => {
    if (prevStep.current !== step) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      prevStep.current = step;
    }
  }, [step]);

  return (
    <Stack w="100%" p="32px 24px" gap="24px" align="center">
      <Stepper w="80%" maxW="1000px" />
      {step === 0 && <SessionDetailsSection />}
      {/* {step === 1 && <EmailConfirmationSection />} */}
      {step === 1 && <QuestionEditorSection />}
      {step === 2 && <ReviewSection onUpdateSuccess={onUpdateSuccess} />}
      {step === 3 && !isUpdateMode && <SuccessSection />}
    </Stack>
  );
}
